/**
 * Thin client for the Payload CMS.
 *
 * Talks to the REST API at VITE_CMS_URL/api/*. Public-readable collections only
 * (anonymous reads). Drafts are not fetched here — Payload's read access on the
 * Dispatches collection already filters them out for anonymous requests.
 */

const CMS_URL = import.meta.env.VITE_CMS_URL ?? "";

if (!CMS_URL && typeof window !== "undefined") {
  console.warn(
    "[cms] VITE_CMS_URL is not set. CMS-backed pages will fail to load.",
  );
}

export type MediaDoc = {
  id: number;
  url?: string | null;
  alt?: string | null;
  width?: number | null;
  height?: number | null;
};

/** Companion-resource mapping: links one YouTube video to one Dispatch. */
export type VideoResourceMapping = {
  id: number;
  youtubeVideo: Video | number;
  dispatch: Dispatch | number;
  ctaText: string;
};

/** YouTube video record mirrored from the channel via the CMS daily sync. */
export type Video = {
  id: number;
  youtubeVideoId: string;
  title: string;
  description?: string | null;
  thumbnailUrl: string;
  channelTitle?: string | null;
  publishedAt: string;
  viewCount: number;
  viewCountFormatted?: string | null;
  durationSeconds: number;
  durationFormatted?: string | null;
  isShort: boolean;
  lastSyncedAt: string;
};

export type Dispatch = {
  id: number;
  title: string;
  slug: string;
  /** Free-form text — drives badge + filter pill. Author may include trailing whitespace; trim on consumer side. */
  type: string;
  publishedDate: string;
  duration?: string | null;
  excerpt?: string | null;
  coverImage?: MediaDoc | number | null;
  /** Raw HTML string authored in the CMS. Must be sanitized before render. */
  body: string;
  _status?: "draft" | "published";
  updatedAt: string;
  createdAt: string;
};

export type PaginatedResponse<T> = {
  docs: T[];
  totalDocs: number;
  totalPages: number;
  page: number;
  hasNextPage: boolean;
  hasPrevPage: boolean;
};

function cmsUrl(path: string, params?: Record<string, string | number | undefined>): string {
  const u = new URL(path, CMS_URL || "http://localhost:3001");
  if (params) {
    for (const [k, v] of Object.entries(params)) {
      if (v !== undefined && v !== null && v !== "") {
        u.searchParams.set(k, String(v));
      }
    }
  }
  return u.toString();
}

/**
 * Payload returns image URLs relative to its own host (e.g. /api/media/file/foo.webp).
 * Prefix with CMS_URL so the frontend can render them.
 */
export function absoluteMediaUrl(url?: string | null): string | null {
  if (!url) return null;
  if (/^https?:\/\//i.test(url)) return url;
  return `${CMS_URL}${url.startsWith("/") ? "" : "/"}${url}`;
}

export type FetchDispatchesOptions = {
  limit?: number;
  page?: number;
  /** Filter by exact type match (case-sensitive, trimmed). Pass undefined to skip. */
  type?: string;
  /** Sort key; default "-publishedDate" (newest first). */
  sort?: string;
};

export async function fetchDispatches({
  limit = 24,
  page = 1,
  type,
  sort = "-publishedDate",
}: FetchDispatchesOptions = {}): Promise<PaginatedResponse<Dispatch>> {
  const params: Record<string, string | number | undefined> = {
    depth: 1,
    limit,
    page,
    sort,
  };
  if (type) {
    params["where[type][equals]"] = type;
  }
  const res = await fetch(cmsUrl("/api/dispatches", params));
  if (!res.ok) {
    throw new Error(`CMS fetch failed (${res.status}) for /api/dispatches`);
  }
  return (await res.json()) as PaginatedResponse<Dispatch>;
}

export async function fetchDispatchBySlug(slug: string): Promise<Dispatch | null> {
  const trimmed = slug.trim();
  if (!trimmed) return null;
  const res = await fetch(
    cmsUrl("/api/dispatches", {
      "where[slug][equals]": trimmed,
      depth: 1,
      limit: 1,
    }),
  );
  if (!res.ok) {
    throw new Error(`CMS fetch failed (${res.status}) for slug "${slug}"`);
  }
  const data = (await res.json()) as PaginatedResponse<Dispatch>;
  return data.docs[0] ?? null;
}

export type FetchVideosOptions = {
  limit?: number;
  page?: number;
  /** Default "-publishedAt" (newest first). */
  sort?: string;
  /** Include Shorts. Default false. */
  includeShorts?: boolean;
};

export async function fetchVideos({
  limit = 24,
  page = 1,
  sort = "-publishedAt",
  includeShorts = false,
}: FetchVideosOptions = {}): Promise<PaginatedResponse<Video>> {
  const params: Record<string, string | number | undefined> = {
    depth: 0,
    limit,
    page,
    sort,
  };
  if (!includeShorts) {
    params["where[isShort][equals]"] = "false";
  }
  const res = await fetch(cmsUrl("/api/videos", params));
  if (!res.ok) {
    throw new Error(`CMS fetch failed (${res.status}) for /api/videos`);
  }
  return (await res.json()) as PaginatedResponse<Video>;
}

/** YouTube watch URL — used to build card links. */
export function youtubeWatchUrl(videoId: string): string {
  return `https://www.youtube.com/watch?v=${encodeURIComponent(videoId)}`;
}

/** Public channel URL — used for "Subscribe on YouTube" CTAs. */
export const VAIBHAV_YOUTUBE_CHANNEL_URL = "https://www.youtube.com/@vaibhavsisinty";

/**
 * Fetches all video → resource mappings.
 *
 * Pages that show video cards (FeaturedThisWeek, VideoLibraryGrid) call this
 * once on mount, build a `Map<youtubeVideoId, mapping>` and look up each card
 * against it. depth=2 populates both the Video and Dispatch sides of the relationship.
 */
export async function fetchVideoResourceMappings(): Promise<
  PaginatedResponse<VideoResourceMapping>
> {
  const res = await fetch(
    cmsUrl("/api/video-resource-mappings", {
      depth: 2,
      limit: 200,
    }),
  );
  if (!res.ok) {
    throw new Error(
      `CMS fetch failed (${res.status}) for /api/video-resource-mappings`,
    );
  }
  return (await res.json()) as PaginatedResponse<VideoResourceMapping>;
}

/**
 * Build an index keyed by YouTube video ID so cards can look up their mapping in O(1).
 * Skips rows whose related video or dispatch failed to populate (defensive).
 */
export function indexVideoResourceMappings(
  mappings: VideoResourceMapping[],
): Map<string, VideoResourceMapping> {
  const idx = new Map<string, VideoResourceMapping>();
  for (const m of mappings) {
    const video =
      typeof m.youtubeVideo === "object" && m.youtubeVideo !== null
        ? m.youtubeVideo
        : null;
    const dispatch =
      typeof m.dispatch === "object" && m.dispatch !== null ? m.dispatch : null;
    if (!video?.youtubeVideoId || !dispatch?.slug) continue;
    idx.set(video.youtubeVideoId, m);
  }
  return idx;
}

/** Reads the dispatch slug out of a populated mapping. Returns null if not populated. */
export function mappedDispatchSlug(m: VideoResourceMapping): string | null {
  if (typeof m.dispatch === "object" && m.dispatch !== null) {
    return m.dispatch.slug ?? null;
  }
  return null;
}

